import type { Action, State } from 'easy-peasy';
import { action, persist } from 'easy-peasy';

/**
 * How far into the track an open playback got, in whole seconds.
 */
export interface OpenAudioPlayback {
    heardUpToSeconds: number;
}

interface PlaybackPayload {
    examPassApiId: string;
    blockApiId: string;
}

/**
 * Addresses one participant's playback of one audio block.
 */
function openPlaybackKey({ examPassApiId, blockApiId }: PlaybackPayload) {
    return `${examPassApiId}.${blockApiId}`;
}

export interface AudioPlaybackModel {
    /**
     * The playbacks of limited audio blocks that are on the clock but not paid
     * for yet, keyed by exam pass and block.
     */
    openPlaybacks: Record<string, OpenAudioPlayback>;

    /**
     * Puts a playback on the clock. Starts nothing itself, the record only
     * tells a reload that a playback is already running.
     */
    markPlaybackStarted: Action<AudioPlaybackModel, PlaybackPayload>;

    /**
     * Moves the resume point forward. Never backwards: a participant must not
     * win back seconds by seeking.
     */
    saveProgress: Action<
        AudioPlaybackModel,
        PlaybackPayload & { heardUpToSeconds: number }
    >;

    /**
     * Drops the record, either because the playback was paid for or because
     * there is nothing left to pay with.
     */
    forgetPlayback: Action<AudioPlaybackModel, PlaybackPayload>;
}

/**
 * Keeps the progress of the limited audio blocks' open playbacks, so that a
 * reload continues a track where it left off instead of restarting it for free.
 */
export const audioPlaybackModel: AudioPlaybackModel = persist(
    {
        openPlaybacks: {},

        markPlaybackStarted: action((state, payload) => {
            const key = openPlaybackKey(payload);

            // A resumed playback already carries its progress; only an
            // untracked one needs a record.
            if (!state.openPlaybacks[key]) {
                state.openPlaybacks[key] = { heardUpToSeconds: 0 };
            }
        }),

        saveProgress: action((state, { heardUpToSeconds, ...payload }) => {
            const key = openPlaybackKey(payload);

            if (
                heardUpToSeconds <=
                (state.openPlaybacks[key]?.heardUpToSeconds ?? 0)
            ) {
                return;
            }

            state.openPlaybacks[key] = { heardUpToSeconds };
        }),

        forgetPlayback: action((state, payload) => {
            delete state.openPlaybacks[openPlaybackKey(payload)];
        }),
    },
    {
        storage: 'localStorage',
        allow: ['openPlaybacks'],
    },
);

/**
 * Reads the open playback of one audio block, or `undefined` if none is on the
 * clock.
 */
export function getOpenPlayback(
    state: State<AudioPlaybackModel>,
    target: PlaybackPayload,
): OpenAudioPlayback | undefined {
    return state.openPlaybacks[openPlaybackKey(target)];
}
